import React from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import { useI18n } from '@whizz/react-i18n';
import { components } from 'react-select';
import classnames from 'classnames';
import type { HTMLAttributes } from 'react';
import type { ControlProps, MenuListComponentProps, OptionProps, OptionTypeBase, Props as SelectProps } from 'react-select';
import type { StudentType } from '../../pages/types';
import { Selector } from '../../components';
import { CustomMenuWithResults, CustomNoOptionsMessage, CustomStudentOption } from '../../components/selector/components';
import { GLOBAL_ICONS } from '../../util/vars';
import { PREFIX, SELECTOR_ICON, SELECTOR_STUDENT_SEARCH, SELECTOR_STUDENT_SEARCH_NO_BORDER } from '../../util/data-id';

type HTMLProps = HTMLAttributes<HTMLDivElement>;
interface Props extends HTMLProps{
  /**
   * Array of students.
   */
  students: StudentType[];
  /**
   * Sets the student search selected value.
   */
  value: OptionTypeBase | null;
  /**
   * Handles change on the search input.
   * @param option
   */
  handleChange(option: OptionTypeBase | null): void;
  /**
   * Sets if the options are loading.
   */
  isFetchingData?: boolean
}

const StudentSearch = ({
  students,
  value,
  handleChange,
  isFetchingData,
  ...other
}: Props): JSX.Element => {

  const { translate } = useI18n();
  const hasValue = null != value;

  return (
    <>
      <GlobalStyle/>
      <SearchWrapper {...other as HTMLProps}>
        <Selector
          id={'students'}
          className={SELECTOR_STUDENT_SEARCH}
          label={'Search students'}
          placeholder={translate('components.SELECTORS.PLACEHOLDERS.STUDENT_SEARCH')}
          hideLabel
          {...hasValue && {
            icon: 'Student3d',
          }}
          {...!hasValue && {
            icon: GLOBAL_ICONS.search,
          }}
          onChange={(option: OptionTypeBase) => handleChange(option)}
          options={students.map((student) => {
            return {
              value: student.id,
              label: student.fullName,
            };
          })}
          customComponents={{
            DropdownIndicator: null,
            Control: (props: ControlProps<OptionTypeBase, false>) => {
              return (
                <components.Control
                  className={classnames({
                    'has_value': hasValue,
                  })}
                  {...props}
                />
              );
            },
            NoOptionsMessage: (props: SelectProps) => {
              const message = isFetchingData
                ? 'components.SELECTORS.STUDENT_SEARCH.FETCHING'
                : 'components.SELECTORS.NO_OPTIONS.STUDENT_SEARCH';
              return (
                <CustomNoOptionsMessage
                  message={translate(message)}
                  {...props}
                />
              );
            },
            Option: (props: OptionProps<OptionTypeBase, false>) => {
              return <CustomStudentOption {...props} />;
            },
            MenuList: (props: MenuListComponentProps<OptionTypeBase, false>) => {
              return <CustomMenuWithResults {...props} />;
            },
          }}
          value={value}
        />
      </SearchWrapper>
    </>
  );
};

const GlobalStyle = createGlobalStyle`
    [data-id=${SELECTOR_STUDENT_SEARCH_NO_BORDER}] .${SELECTOR_STUDENT_SEARCH} {
      .${PREFIX}__control {
        border-color: transparent;
      }
    }
  `;

const SearchWrapper = styled('div')`
  [data-id='${SELECTOR_ICON}'] {
    [data-id='whizz_icon-student_background'] {
      fill: ${(props) => props.theme.colors.secondary} !important;
    }        
  }  
`;

StudentSearch.displayName = 'StudentSearch';

export default StudentSearch;
